import { shouldExcludeFromPrefetch } from "@theme/pjax-routing";

const HOVER_DELAY_MS = 65;

class LinkPrefetcher {
  #prefetched = new Set();
  #pendingPrecache = new Map();
  #intersectionObserver;
  #mutationObserver;
  #hoverTimers = new WeakMap();

  constructor() {
    this.#prefetched.add(location.href);
    if (navigator.connection?.saveData) return;

    document.addEventListener("pjax:ready", () => this.#flushPending(), { once: true });

    this.#intersectionObserver = new IntersectionObserver(this.#onIntersect, {
      rootMargin: "0px",
      threshold: 0,
    });

    this.#scan(document);

    this.#mutationObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1) this.#scan(node);
        }
      }
    });
    this.#mutationObserver.observe(document.body, { subtree: true, childList: true });
  }

  #flushPending() {
    if (!window.__pjax) return;
    for (const [url, html] of this.#pendingPrecache) {
      window.__pjax.precache(url, html);
    }
    this.#pendingPrecache.clear();
  }

  #mode(el) {
    return (el.getAttribute("data-prefetch") || "viewport").toLowerCase();
  }

  #scan(root) {
    const handle = (el) => {
      if (!this.#isCandidate(el)) return;
      const mode = this.#mode(el);
      if (mode === "none") return;
      if (mode === "immediate") {
        this.#prefetch(el.href);
        return;
      }
      if (mode === "hover") {
        this.#attachHover(el);
        return;
      }
      this.#intersectionObserver.observe(el);
    };

    if (root.matches?.("a[href]")) handle(root);
    root.querySelectorAll?.("a[href]").forEach(handle);
  }

  #isCandidate(el) {
    if (el.target === "_blank") return false;
    if (el.hasAttribute("download")) return false;
    const raw = el.getAttribute("href") ?? "";
    if (!raw || raw.startsWith("#")) return false;
    if (/^(mailto:|tel:|javascript:)/i.test(raw)) return false;
    if (shouldExcludeFromPrefetch(el.href)) return false;
    return true;
  }

  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      this.#prefetch(entry.target.href);
      this.#intersectionObserver.unobserve(entry.target);
    }
  };

  #attachHover(el) {
    const start = () => {
      const timer = setTimeout(() => this.#prefetch(el.href), HOVER_DELAY_MS);
      this.#hoverTimers.set(el, timer);
    };
    const cancel = () => {
      const timer = this.#hoverTimers.get(el);
      if (timer) clearTimeout(timer);
      this.#hoverTimers.delete(el);
    };
    el.addEventListener("mouseenter", start);
    el.addEventListener("focusin", start);
    el.addEventListener("mouseleave", cancel);
    el.addEventListener("focusout", cancel);
  }

  async #prefetch(href) {
    let url;
    try {
      url = new URL(href, location.href);
    } catch {
      return;
    }
    if (url.href === location.href) return;
    if (this.#prefetched.has(url.href)) return;
    if (window.__pjax?.cache?.has(url.href)) {
      this.#prefetched.add(url.href);
      return;
    }
    this.#prefetched.add(url.href);

    try {
      const response = await fetch(url.href, {
        method: "GET",
        credentials: "same-origin",
        priority: "low",
        headers: { "X-Requested-With": "PjaxMax" },
      });
      if (!response.ok) return;
      const html = await response.text();
      if (window.__pjax) {
        window.__pjax.precache(url.href, html);
      } else {
        this.#pendingPrecache.set(url.href, html);
      }
    } catch {
      // swallow prefetch failures — they shouldn't disrupt the page
    }
  }
}

new LinkPrefetcher();
